import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
} from "react";
import api, { TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refreshUser = useCallback(async () => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
            setUser(null);
            setLoading(false);
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data.user);
            return data.user;
        } catch (e) {
            localStorage.removeItem(TOKEN_KEY);
            setUser(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshUser();
    }, [refreshUser]);

    const signup = useCallback(async (payload) => {
        const { data } = await api.post("/auth/signup", payload);
        localStorage.setItem(TOKEN_KEY, data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem(TOKEN_KEY, data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
    }, []);

    const activate = useCallback(async () => {
        const { data } = await api.post("/account/activate");
        setUser(data.user);
        return data.user;
    }, []);

    const updateDownloadUrls = useCallback(async (urls) => {
        const { data } = await api.put("/account/download-urls", urls);
        setUser(data.user);
        return data.user;
    }, []);

    const value = {
        user,
        loading,
        isAuthenticated: !!user,
        refreshUser,
        signup,
        login,
        logout,
        activate,
        updateDownloadUrls,
    };

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
